import React, { useEffect, useRef, useState } from 'react';

export type SheetSnap = 'peek' | 'half' | 'full';

interface MobileSheetProps<T extends string> {
  tabs: Array<{ value: T; label: React.ReactNode; disabled?: boolean }>;
  tab: T;
  onTab: (tab: T) => void;
  snap: SheetSnap;
  onSnap: (snap: SheetSnap) => void;
  /** one line shown under the tabs when collapsed */
  peek?: React.ReactNode;
  /** container height the sheet lives in */
  containerHeight: number;
  children: React.ReactNode;
  /** the active tab manages its own scrolling (e.g. a chat) */
  fillContent?: boolean;
}

const PEEK = 104;

/**
 * Persistent bottom sheet for phones: the canvas stays interactive above it.
 * Drag the handle or tabs between peek, half and full; tap a tab to open it.
 */
export function MobileSheet<T extends string>({ tabs, tab, onTab, snap, onSnap, peek, containerHeight, children, fillContent }: MobileSheetProps<T>) {
  const heights: Record<SheetSnap, number> = {
    peek: PEEK,
    half: Math.round(containerHeight * 0.52),
    full: Math.max(PEEK, containerHeight - 8),
  };
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const drag = useRef<{ startY: number; startH: number; at: number; lastY: number; moved: boolean } | null>(null);
  const height = dragHeight ?? heights[snap];

  useEffect(() => {
    setDragHeight(null);
  }, [snap]);

  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('[role="tab"]') && e.pointerType === 'mouse') return;
    drag.current = { startY: e.clientY, startH: heights[snap], at: performance.now(), lastY: e.clientY, moved: false };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.abs(dy) < 6) return;
    if (!d.moved) (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    d.moved = true;
    d.lastY = e.clientY;
    setDragHeight(Math.max(PEEK - 20, Math.min(heights.full, d.startH - dy)));
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    if (!d || !d.moved) return;
    const h = d.startH - (e.clientY - d.startY);
    const velocity = (e.clientY - d.startY) / Math.max(1, performance.now() - d.at);
    let next: SheetSnap;
    if (velocity > 0.5) next = snap === 'full' ? 'half' : 'peek';
    else if (velocity < -0.5) next = snap === 'peek' ? 'half' : 'full';
    else {
      next = (Object.keys(heights) as SheetSnap[]).reduce((best, key) => (Math.abs(heights[key] - h) < Math.abs(heights[best] - h) ? key : best), 'peek' as SheetSnap);
    }
    setDragHeight(null);
    onSnap(next);
  };

  return (
    <div
      className="absolute inset-x-0 bottom-0 z-30 flex flex-col rounded-t-[22px] bg-white shadow-[0_-12px_40px_-16px_rgba(0,0,0,0.25)] ring-1 ring-black/[0.06]"
      style={{
        height,
        transition: dragHeight === null ? 'height 340ms cubic-bezier(0.16, 1, 0.3, 1)' : 'none',
      }}
    >
      <div className="flex-shrink-0 touch-none select-none" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
        <button
          type="button"
          aria-label={snap === 'peek' ? 'Expand panel' : 'Collapse panel'}
          onClick={() => onSnap(snap === 'peek' ? 'half' : 'peek')}
          className="flex h-5 w-full items-center justify-center"
        >
          <span className="h-1 w-9 rounded-full bg-neutral-300" />
        </button>
        <div role="tablist" aria-label="Panels" className="flex items-center gap-1 px-3">
          {tabs.map((t) => {
            const active = t.value === tab;
            return (
              <button
                key={t.value}
                type="button"
                role="tab"
                aria-selected={active}
                disabled={t.disabled}
                onClick={() => {
                  onTab(t.value);
                  if (snap === 'peek') onSnap('half');
                }}
                className={`inline-flex h-9 min-w-0 items-center gap-1.5 rounded-full px-3 text-[13px] transition-colors disabled:opacity-40 ${
                  active ? 'bg-neutral-100 font-medium text-ink' : 'text-neutral-500'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {snap === 'peek' && peek && <div className="truncate px-4 pb-2 pt-1.5 text-[12.5px] text-neutral-500">{peek}</div>}
      </div>
      <div
        className={`min-h-0 flex-1 ${fillContent ? 'flex flex-col' : 'overflow-y-auto overscroll-contain'} ${snap === 'peek' && dragHeight === null ? 'invisible' : ''}`}
        style={{ paddingBottom: fillContent ? undefined : 'max(12px, env(safe-area-inset-bottom))' }}
      >
        {children}
      </div>
    </div>
  );
}
